import React from 'react';
import { useNavigate } from 'react-router';

import { getAccountUuid, isFeatureEnabled } from 'ff/common/ffAppUtils';

import { buildShareUrl, extractTrimmedValues } from 'ff/debrief/utils';
import { useDebriefDispatch, useRouteContext } from 'ff/debrief/hooks';
import { actions as chronoActions } from 'ff/debrief/features/ChronoSlice';
import {
    TracklogOtherInfoEditable,
    useGetAllTracklogsQuery,
    useGetAllSharedTracklogsQuery,
    useGetShareStateQuery,
    useGetTracklogByIdQuery,
    useImportG1000TracklogMutation,
    useMakeDebriefPrivateMutation,
    useMakeDebriefPublicMutation,
    useUpdateTracklogOtherInfoMutation,
} from 'ff/debrief/features/TracklogApi';

interface CurrentTracklogOptions {
    autoSelect?: boolean;
}

export const useCurrentTracklog = ({
    autoSelect,
}: CurrentTracklogOptions = {}) => {
    const navigate = useNavigate();

    const dispatch = useDebriefDispatch();
    const { selectedTracklogId, isShared } = useRouteContext();

    const { data: tlList, refetch: refetchTracklogList } =
        useGetAllTracklogsQuery({
            page: 0, // Page is ignored here
            uuid: selectedTracklogId,
        });
    const { data: sharedList } = useGetAllSharedTracklogsQuery();
    const {
        data: tl,
        currentData: currentTl,
        ...tracklogQueryStatus
    } = useGetTracklogByIdQuery(selectedTracklogId, {
        skip: !selectedTracklogId,
    });
    const { currentData: share, ...shareQueryStatus } = useGetShareStateQuery(
        selectedTracklogId,
        {
            skip:
                !isFeatureEnabled('new_share_debrief_feature') ||
                !selectedTracklogId,
        }
    );
    const [setPublic] = useMakeDebriefPublicMutation();
    const [setPrivate] = useMakeDebriefPrivateMutation();
    const [updateTracklogOtherInfo] = useUpdateTracklogOtherInfoMutation();

    const [importG1000Tracklog] = useImportG1000TracklogMutation();

    const isPublic = shareQueryStatus.isSuccess;

    // Auto select first tracklog on initial load
    const isListLoaded = Boolean(tlList) && tlList.tracklogs.length > 0;
    React.useEffect(() => {
        if (autoSelect && isListLoaded && !selectedTracklogId) {
            setTracklog(tlList.tracklogs[0].trackUuid);
        }
    }, [autoSelect, isListLoaded, selectedTracklogId]);

    React.useEffect(() => {
        if (
            autoSelect &&
            isListLoaded &&
            selectedTracklogId &&
            tracklogQueryStatus.isError
        ) {
            const found = tlList.tracklogs.find(
                t => t.trackUuid === selectedTracklogId
            );
            if (!found) {
                setTracklog(tlList.tracklogs[0].trackUuid);
            }
        }
    }, [isListLoaded, selectedTracklogId, tracklogQueryStatus.isError]);

    const setTracklog = (trackId: string) => {
        navigate(`/${trackId}`);
        dispatch(chronoActions.reset());
    };

    const setSharedTracklog = (shareId: string) => {
        navigate(`/shared/${shareId}`);
        dispatch(chronoActions.reset());
    };

    const deselect = () => {
        const selectedIndex = tlList?.tracklogs.findIndex(
            t => t.trackUuid === selectedTracklogId
        );
        const nextTracklog = tlList?.tracklogs[selectedIndex + 1];
        const prevTracklog = tlList?.tracklogs[selectedIndex - 1];
        if (nextTracklog) setTracklog(nextTracklog.trackUuid);
        else if (prevTracklog) setTracklog(prevTracklog.trackUuid);
        else setTracklog('');
    };

    const deselectShared = () => {
        const selectedIndex = sharedList?.findIndex(
            s => s.tracklogSummary.trackUuid === selectedTracklogId
        );
        const nextShared = sharedList?.[selectedIndex + 1];
        const prevShared = sharedList?.[selectedIndex - 1];
        if (nextShared) setSharedTracklog(nextShared.shareUuid);
        else if (prevShared) setSharedTracklog(prevShared.shareUuid);
        else setSharedTracklog('');
    };

    const setShareState = (newPublic: boolean) => {
        if (newPublic) {
            return setPublic(selectedTracklogId).unwrap();
        } else {
            return setPrivate(share.shareId).unwrap();
        }
    };

    const updateOtherInfo = (
        updates: Partial<TracklogOtherInfoEditable>
    ): Promise<null> => {
        return updateTracklogOtherInfo({
            objectId: tl?.metadata.objectId,
            tracklogId: tl?.trackUuid,
            updates,
        }).unwrap();
    };

    const shareUrl = buildShareUrl(share?.shareId);
    const hasAssociation = tl?.logbookAssociationDetails?.length > 0;

    return {
        tracklog: { ...tl, trackUuid: selectedTracklogId },
        currentTracklog: currentTl,
        currentPage: tlList?.page,
        trimmedValues: extractTrimmedValues(tl),
        metadata: tl?.metadata,
        hasCloudAhoyDebrief: Boolean(
            tl?.analysisSummary && tl?.analysisSummary.status !== 'ERROR'
        ),
        shareUrl: shareUrl,
        hasAssociation: hasAssociation,
        isFailedUpload: tracklogQueryStatus.isError,
        isOwnTracklog: getAccountUuid() === tl?.accountUuid,
        isLoaded: !!currentTl,
        accuracy: tl?.accuracy,
        points: tl?.points,
        coordinates: tl?.coordinates,
        viewport: tl?.viewport,
        isPublic: isPublic,
        isShared: isShared,
        setTracklog: setTracklog,
        setSharedTracklog: setSharedTracklog,
        setShareState: setShareState,
        deselect: deselect,
        deselectShared: deselectShared,
        updateOtherInfo: updateOtherInfo,
        status: tracklogQueryStatus,
        importG1000Tracklog: importG1000Tracklog,
        refetchTracklogList: refetchTracklogList,
    };
};
